import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../api.js';
import { fmtIST, fmtISTShort } from '../lib/datetime.js';

export default function Reports({ onOpenCampaign }) {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(true);
  const [scope, setScope] = useState('all');

  async function load() {
    setBusy(true);
    try {
      const url = scope === 'all'
        ? '/api/reports/overview'
        : `/api/reports/overview?campaign_id=${Number(scope)}`;
      setR(await api.get(url));
    }
    finally { setBusy(false); }
  }
  useEffect(() => { load(); /* eslint-disable-line */ }, [scope]);
  useEffect(() => { const t = setInterval(load, 30000); return () => clearInterval(t); /* eslint-disable-line */ }, [scope]);

  return (
    <div className="px-8 py-6 max-w-[1300px] mx-auto">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Reports</h1>
          <p className="text-sm text-slate-500 mt-0.5">{scope === 'all' ? 'Cross-campaign overview — channel performance, conversion, and top failures.' : 'Filtered to one campaign — top stats reflect that campaign only.'}</p>
        </div>
        <div className="flex items-center gap-2">
          <select className="input h-9 text-sm" value={scope} onChange={e => setScope(e.target.value)}>
            <option value="all">All campaigns</option>
            {(r?.campaigns || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button className="btn-ghost btn-sm" onClick={load} disabled={busy}>{busy ? 'Loading…' : 'Refresh'}</button>
        </div>
      </div>

      <AccountHealth />

      {!r ? (
        <div className="card p-12 text-center text-slate-400 text-sm">{busy ? 'Loading…' : 'No data.'}</div>
      ) : (
        <div className="space-y-5">
          <TopStats t={r.totals} />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <ChannelMix wa={r.by_channel.wa || {}} email={r.by_channel.email || {}} />
            <SourceSplit s={r.submissions_by_source} className="lg:col-span-1" />
            <TopFailures rows={r.top_failures} className="lg:col-span-1" />
          </div>
          <CampaignTable campaigns={r.campaigns} onOpen={onOpenCampaign} />
        </div>
      )}
    </div>
  );
}

/* ============================================================================
 * Top-line stats
 * ============================================================================ */
function TopStats({ t }) {
  const deliveryRate   = t.sends > 0 ? t.delivered / t.sends : 0;
  const readRate       = t.delivered > 0 ? t.read_or_opened / t.delivered : 0;
  const conversionRate = t.delivered > 0 ? t.submitted / t.delivered : 0;
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
      <Stat label="Contacts"  value={t.contacts} hint={`${t.campaigns_total} campaigns · ${t.campaigns_active} active`} />
      <Stat label="Sends"     value={t.sends}    hint={`${t.sent.toLocaleString()} sent · ${t.failed.toLocaleString()} failed`} />
      <Stat label="Delivery rate" value={pct(deliveryRate)}     tone="emerald" hint={`${t.delivered.toLocaleString()} of ${t.sends.toLocaleString()}`} />
      <Stat label="Read / Open"   value={pct(readRate)}         tone="sky"     hint={`${t.read_or_opened.toLocaleString()} of delivered`} />
      <Stat label="Conversion"    value={pct(conversionRate)}   tone="brand"   hint={`${t.submitted.toLocaleString()} submitted of delivered`} />
    </div>
  );
}

function Stat({ label, value, tone = 'slate', hint }) {
  const tones = {
    slate:   'text-slate-900',
    emerald: 'text-emerald-700',
    sky:     'text-sky-700',
    brand:   'text-brand-700',
    rose:    'text-rose-700',
  };
  return (
    <div className="card p-4">
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-1 text-2xl font-semibold tabular-nums ${tones[tone]}`}>{
        typeof value === 'number' ? value.toLocaleString() : value
      }</div>
      {hint && <div className="text-[11px] text-slate-500 mt-1 truncate" title={hint}>{hint}</div>}
    </div>
  );
}

function pct(v) {
  const n = (v || 0) * 100;
  if (n === 0) return '0%';
  if (n >= 1) return `${Math.round(n)}%`;
  // Sub-1% — keep one decimal so small but real rates don't read as 0%.
  return `${n.toFixed(1)}%`;
}

/* ============================================================================
 * Channel comparison (WA vs Email)
 * ============================================================================ */
function ChannelMix({ wa, email }) {
  return (
    <div className="card p-5 lg:col-span-1">
      <h3 className="text-sm font-semibold text-slate-900 mb-3">Channel performance</h3>
      <ChannelRow name="WhatsApp" data={wa} engLabel="Read" tone="emerald" />
      <div className="border-t border-slate-100 my-3" />
      <ChannelRow name="Email"    data={email} engLabel="Opened" tone="sky" />
    </div>
  );
}

function ChannelRow({ name, data, engLabel, tone }) {
  const sends     = data.sends || 0;
  const delivered = data.delivered || 0;
  const read      = data.read_or_opened || 0;
  const dr = sends     > 0 ? delivered / sends : 0;
  const rr = delivered > 0 ? read / delivered  : 0;
  const dotCls = tone === 'sky' ? 'bg-sky-500' : 'bg-emerald-500';
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <span className={`w-2 h-2 rounded-full ${dotCls}`} />
        <span className="text-sm font-semibold text-slate-900">{name}</span>
        <span className="text-xs text-slate-500 ml-auto">{sends.toLocaleString()} sends</span>
      </div>
      <Bar label="Delivered"   value={delivered} max={sends}     pct={dr} tone={tone} />
      <Bar label={engLabel}    value={read}      max={delivered} pct={rr} tone="slate" />
    </div>
  );
}

function Bar({ label, value, max, pct, tone = 'slate' }) {
  const w = Math.max(2, Math.round(pct * 100));
  const bg = tone === 'emerald' ? 'bg-emerald-500'
    : tone === 'sky' ? 'bg-sky-500'
    : 'bg-slate-400';
  return (
    <div className="mb-1.5 last:mb-0">
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-slate-600">{label}</span>
        <span className="text-slate-500 tabular-nums">{value.toLocaleString()} <span className="text-slate-400 ml-1">{Math.round(pct * 100)}%</span></span>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${bg}`} style={{ width: `${w}%` }} />
      </div>
    </div>
  );
}

/* ============================================================================
 * Source-channel split for form submissions (WA vs Email vs Direct).
 * ============================================================================ */
function SourceSplit({ s }) {
  const total = (s.wa || 0) + (s.email || 0) + (s.direct || 0);
  const pcts = {
    wa:     total > 0 ? Math.round((s.wa || 0) / total * 100) : 0,
    email:  total > 0 ? Math.round((s.email || 0) / total * 100) : 0,
    direct: total > 0 ? Math.round((s.direct || 0) / total * 100) : 0,
  };
  return (
    <div className="card p-5">
      <h3 className="text-sm font-semibold text-slate-900 mb-3">Submissions by source</h3>
      <div className="text-3xl font-semibold text-slate-900 mb-3 tabular-nums">{total.toLocaleString()}</div>
      <div className="flex h-3 rounded-full overflow-hidden bg-slate-100 mb-3">
        <div className="bg-emerald-500" style={{ width: `${pcts.wa}%` }} />
        <div className="bg-sky-500"     style={{ width: `${pcts.email}%` }} />
        <div className="bg-slate-300"   style={{ width: `${pcts.direct}%` }} />
      </div>
      <ul className="text-xs space-y-1.5">
        <SrcRow label="WhatsApp" count={s.wa || 0}    pct={pcts.wa}     dotCls="bg-emerald-500" />
        <SrcRow label="Email"    count={s.email || 0} pct={pcts.email}  dotCls="bg-sky-500" />
        <SrcRow label="Direct"   count={s.direct || 0} pct={pcts.direct} dotCls="bg-slate-300" />
      </ul>
    </div>
  );
}

function SrcRow({ label, count, pct, dotCls }) {
  return (
    <li className="flex items-center gap-2">
      <span className={`w-1.5 h-1.5 rounded-full ${dotCls}`} />
      <span className="text-slate-700">{label}</span>
      <span className="flex-1" />
      <span className="text-slate-500 tabular-nums">{count.toLocaleString()}</span>
      <span className="text-slate-400 tabular-nums w-9 text-right">{pct}%</span>
    </li>
  );
}

/* ============================================================================
 * Top failure codes (across all campaigns)
 * ============================================================================ */
function TopFailures({ rows }) {
  return (
    <div className="card p-5">
      <h3 className="text-sm font-semibold text-slate-900 mb-3">Top failure codes</h3>
      {!rows.length ? (
        <div className="text-xs text-slate-400 py-6 text-center">No failures so far.</div>
      ) : (
        <ul className="text-xs space-y-2">
          {rows.map((r, i) => (
            <li key={i} className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 w-12">{r.channel}</span>
              <code className="font-mono">{r.error_code}</code>
              <span className="flex-1" />
              <span className="text-rose-700 tabular-nums font-semibold">{r.count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ============================================================================
 * Campaign comparison table
 * ============================================================================ */
function CampaignTable({ campaigns, onOpen }) {
  const [sortBy, setSortBy] = useState('id_desc');

  const sorted = useMemo(() => {
    const a = [...campaigns];
    switch (sortBy) {
      case 'sends':         return a.sort((x, y) => y.sends - x.sends);
      case 'delivery_rate': return a.sort((x, y) => y.delivery_rate - x.delivery_rate);
      case 'conversion':    return a.sort((x, y) => {
        const xr = x.delivered > 0 ? x.submitted / x.delivered : 0;
        const yr = y.delivered > 0 ? y.submitted / y.delivered : 0;
        return yr - xr;
      });
      case 'name':          return a.sort((x, y) => x.name.localeCompare(y.name));
      default:              return a;
    }
  }, [campaigns, sortBy]);

  return (
    <div className="card overflow-hidden">
      <div className="px-5 py-3.5 flex items-center justify-between border-b border-slate-100">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Campaigns ({campaigns.length})</h3>
          <p className="text-xs text-slate-500 mt-0.5">All campaigns excluding the test pseudo-campaign.</p>
        </div>
        <select className="input h-8 text-xs" value={sortBy} onChange={e => setSortBy(e.target.value)}>
          <option value="id_desc">Newest first</option>
          <option value="sends">Most sends</option>
          <option value="delivery_rate">Highest delivery</option>
          <option value="conversion">Highest conversion</option>
          <option value="name">Name A–Z</option>
        </select>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-3 py-2.5 text-left">Campaign</th>
              <th className="px-3 py-2.5 text-left">Status</th>
              <th className="px-3 py-2.5 text-right border-l border-slate-200">WhatsApp</th>
              <th className="px-3 py-2.5 text-right border-l border-slate-200">Email</th>
              <th className="px-3 py-2.5 text-right border-l border-slate-200">Submitted</th>
              <th className="px-3 py-2.5 text-right">Conv</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sorted.map(c => (
              <tr key={c.id} className={`align-top ${onOpen ? 'hover:bg-brand-50/40 cursor-pointer' : 'hover:bg-slate-50/60'}`} onClick={() => onOpen && onOpen(c.id)}>
                <td className="px-3 py-2.5">
                  <div className="font-medium text-slate-900 truncate max-w-[280px] flex items-center gap-1.5">
                    {c.name}
                    {onOpen && <span className="text-slate-300">→</span>}
                  </div>
                  <div className="text-[11px] text-slate-500">{fmtISTShort(c.created_at)}</div>
                </td>
                <td className="px-3 py-2.5"><StatusBadge status={c.status} /></td>
                <td className="px-3 py-2.5 border-l border-slate-200">
                  <ChannelCell data={c.wa}     enabled={!!c.send_wa}    engKey="read"   tone="emerald" />
                </td>
                <td className="px-3 py-2.5 border-l border-slate-200">
                  <ChannelCell data={c.email}  enabled={!!c.send_email} engKey="opened" tone="sky" />
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums text-brand-700 font-medium border-l border-slate-200">{c.submitted.toLocaleString()}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{pct(c.delivered > 0 ? c.submitted / c.delivered : 0)}</td>
              </tr>
            ))}
            {!sorted.length && <tr><td colSpan={6} className="px-3 py-12 text-center text-slate-400">No campaigns yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// One channel's column in the campaign comparison table — sends, delivered
// with %, engagement (read for WA / opened for email) with %, and a small
// red failed count if any. Renders a dim "—" when the channel is disabled
// for this campaign.
function ChannelCell({ data, enabled, engKey, tone }) {
  if (!enabled) return <div className="text-right text-xs text-slate-300">— off —</div>;
  if (data.sends === 0) return <div className="text-right text-xs text-slate-400">no sends yet</div>;
  const dr = data.sends > 0     ? data.delivered     / data.sends         : 0;
  const er = data.delivered > 0 ? (data[engKey] || 0) / data.delivered    : 0;
  // Conversion is over delivered (people who actually got the message),
  // matching the top-of-page Conversion stat.
  const cr = data.delivered > 0 ? (data.submitted || 0) / data.delivered  : 0;
  const engLabel = engKey === 'opened' ? 'opened' : 'read';
  const engColor = tone === 'sky' ? 'text-sky-700' : 'text-emerald-700';
  return (
    <div className="text-right text-xs leading-tight tabular-nums">
      <div className="text-slate-500"><span className="text-slate-700">{data.sends.toLocaleString()}</span> sends</div>
      <div><span className={engColor}>{data.delivered.toLocaleString()}</span> <span className="text-slate-400">delivered ({pct(dr)})</span></div>
      {data[engKey] > 0 && <div><span className={engColor}>{data[engKey].toLocaleString()}</span> <span className="text-slate-400">{engLabel} ({pct(er)})</span></div>}
      {data.submitted > 0 && <div className="text-brand-700 font-medium">{data.submitted.toLocaleString()} submitted <span className="text-slate-400 font-normal">({pct(cr)} conv)</span></div>}
      {data.failed > 0 && <div className="text-rose-700">{data.failed.toLocaleString()} failed</div>}
    </div>
  );
}

/* ============================================================================
 * Account health — Meta phone-number quality + recent alerts
 * ============================================================================ */
function AccountHealth() {
  const [alerts, setAlerts] = useState([]);
  const [phone, setPhone]   = useState(null);
  const [showAll, setShowAll] = useState(false);

  async function load() {
    try {
      const a = await api.get(`/api/whatsapp/alerts${showAll ? '?include_resolved=1' : ''}`);
      setAlerts(a.rows || []);
      const p = await api.get('/api/whatsapp/phone-quality').catch(() => null);
      if (p?.phone) setPhone(p.phone);
    } catch {}
  }
  useEffect(() => { load(); /* eslint-disable-line */ }, [showAll]);

  async function dismiss(id) {
    try { await api.post(`/api/whatsapp/alerts/${id}/resolve`); load(); }
    catch (e) { alert(e.body?.error || e.message); }
  }

  const critical = alerts.filter(a => !a.resolved_at && a.severity === 'critical');
  const warning  = alerts.filter(a => !a.resolved_at && a.severity === 'warning');
  const tone = critical.length ? 'rose' : warning.length ? 'amber' : 'emerald';

  if (!alerts.length && !phone) return null;

  return (
    <div className="mb-5 grid grid-cols-1 lg:grid-cols-3 gap-3">
      {phone && (
        <div className={`card p-4 ${tone === 'rose' ? 'border-rose-200 bg-rose-50/50' : tone === 'amber' ? 'border-amber-200 bg-amber-50/50' : 'border-emerald-200 bg-emerald-50/40'}`}>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] uppercase tracking-wider text-slate-500">WhatsApp account</span>
            <QualityPill tier={phone.quality_rating} />
          </div>
          <div className="text-sm font-semibold text-slate-900">{phone.display_phone_number || '—'}</div>
          <div className="text-xs text-slate-600 mt-0.5">{phone.verified_name || ''}</div>
          {phone.messaging_limit_tier && <div className="text-[11px] text-slate-500 mt-1.5">Tier: <strong>{phone.messaging_limit_tier}</strong></div>}
        </div>
      )}
      <div className={`card p-4 lg:col-span-2 ${critical.length ? 'border-rose-200 bg-rose-50/30' : warning.length ? 'border-amber-200 bg-amber-50/30' : ''}`}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-slate-900">Recent alerts</h3>
          <label className="text-[11px] text-slate-500 inline-flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} />
            Include resolved
          </label>
        </div>
        {!alerts.length ? (
          <div className="text-xs text-slate-400">No alerts. Account is healthy.</div>
        ) : (
          <ul className="space-y-1.5 text-xs max-h-40 overflow-y-auto">
            {alerts.slice(0, 8).map(a => (
              <li key={a.id} className={`flex items-start gap-2 p-2 rounded ${a.resolved_at ? 'bg-slate-50/50 opacity-60' : ''}`}>
                <SeverityBadge s={a.severity} />
                <div className="flex-1 min-w-0">
                  <div className="text-slate-800 truncate" title={a.message}>{a.message}</div>
                  <div className="text-[10px] text-slate-400">{a.field} · {fmtISTShort(a.created_at)}</div>
                </div>
                {!a.resolved_at && (
                  <button className="text-[11px] text-slate-500 hover:text-slate-800" onClick={() => dismiss(a.id)}>Dismiss</button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function QualityPill({ tier }) {
  const t = (tier || '').toUpperCase();
  if (t === 'GREEN')  return <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">Green</span>;
  if (t === 'YELLOW') return <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Yellow</span>;
  if (t === 'RED')    return <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">Red</span>;
  return <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{t || 'unknown'}</span>;
}

function SeverityBadge({ s }) {
  const cls = s === 'critical' ? 'bg-rose-100 text-rose-700'
    : s === 'warning' ? 'bg-amber-100 text-amber-700'
    : 'bg-slate-100 text-slate-600';
  return <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${cls} shrink-0`}>{s}</span>;
}

function StatusBadge({ status }) {
  const map = {
    draft:     'bg-slate-100 text-slate-600',
    queued:    'bg-amber-50 text-amber-700',
    running:   'bg-emerald-50 text-emerald-700',
    paused:    'bg-amber-50 text-amber-700',
    done:      'bg-slate-100 text-slate-500',
    cancelled: 'bg-slate-100 text-slate-500',
  };
  return <span className={`text-[11px] uppercase tracking-wider px-1.5 py-0.5 rounded ${map[status] || 'bg-slate-100 text-slate-600'}`}>{status}</span>;
}
